import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePostHog } from 'posthog-js/react'
import type { Survey, SurveyQuestion } from 'posthog-js'
import DOMPurify from 'dompurify'
import { Base, CrossIcon } from '@itsrever/design-system'

// The posthog-js types we pin predate per-question translations.
type Step = SurveyQuestion & {
  translations?: Record<string, Partial<SurveyQuestion>>
}

// An announcement is a PostHog `api` survey made only of link questions, one per page: PostHog
// picks who sees it and when, and this draws it without PostHog's branding.
const isAnnouncement = (survey: Survey) =>
  survey.type === 'api' && survey.questions.every((q) => q.type === 'link')

// The first image of a page's description is its illustration, drawn above the text.
const splitIllustration = (description: string) => {
  const body = DOMPurify.sanitize(description, {
    RETURN_DOM: true
  }) as HTMLElement
  const image = body.querySelector('img')
  image?.remove()
  return { image: image?.getAttribute('src'), html: body.innerHTML }
}

export const Announcement = () => {
  const { t, i18n } = useTranslation()
  const posthog = usePostHog()
  const [survey, setSurvey] = useState<Survey>()
  const [visible, setVisible] = useState(false)
  const [step, setStep] = useState(0)

  // Flags reload once the user is identified, and they decide who the survey targets.
  useEffect(
    () =>
      posthog.onFeatureFlags(() =>
        posthog.getActiveMatchingSurveys((surveys) =>
          setSurvey((current) => current ?? surveys.find(isAnnouncement))
        )
      ),
    [posthog]
  )

  useEffect(() => {
    if (!survey) return
    const timer = setTimeout(
      () => {
        setVisible(true)
        posthog.capture('survey shown', {
          $survey_id: survey.id,
          $survey_name: survey.name
        })
      },
      (survey.appearance?.surveyPopupDelaySeconds ?? 0) * 1000
    )
    return () => clearTimeout(timer)
  }, [survey, posthog])

  if (!survey || !visible) return null

  const close = (action: 'dismissed' | 'responded') => {
    posthog.capture(
      action === 'responded' ? 'survey sent' : 'survey dismissed',
      {
        $survey_id: survey.id,
        $survey_name: survey.name,
        $set: { [`$survey_${action}/${survey.id}`]: true }
      }
    )
    // posthog-js's own key: the next page load skips the survey before the person property syncs.
    try {
      localStorage.setItem(`seenSurvey_${survey.id}`, 'true')
    } catch {
      // Storage can be blocked; the person property still hides it.
    }
    setVisible(false)
  }

  const question = survey.questions[step] as Step
  const page = { ...question, ...question.translations?.[i18n.language] }
  const { image, html } = splitIllustration(page.description ?? '')
  const isLast = step === survey.questions.length - 1

  return (
    <div
      role="dialog"
      aria-label={page.question}
      className="fixed bottom-24 right-6 z-50 w-96 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl"
    >
      <Base.IconButton
        aria-label={t('announcement.close')}
        size="sm"
        onClick={() => close('dismissed')}
        className="absolute right-2 top-2"
      >
        <CrossIcon />
      </Base.IconButton>
      {image ? <img src={image} alt="" className="w-full" /> : null}
      <div className="p-4">
        <p className="text-xs text-neutral-400">
          {t('announcement.step', {
            current: step + 1,
            total: survey.questions.length
          })}
        </p>
        <p className="mt-1 text-base font-medium text-neutral-900">
          {page.question}
        </p>
        <div
          className="mt-2 text-sm text-neutral-600"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <div className="mt-4 flex items-center justify-between">
          <div className="flex gap-1.5">
            {survey.questions.map((q, i) => (
              <span
                key={q.id}
                className={
                  i === step
                    ? 'size-2 rounded-full bg-primary-base'
                    : 'size-2 rounded-full bg-neutral-200'
                }
              />
            ))}
          </div>
          <div className="flex gap-2">
            {step > 0 ? (
              <Base.Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setStep(step - 1)}
              >
                {t('announcement.back')}
              </Base.Button>
            ) : null}
            <Base.Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => (isLast ? close('responded') : setStep(step + 1))}
            >
              {page.buttonText}
            </Base.Button>
          </div>
        </div>
      </div>
    </div>
  )
}
